import type {
  DocAccessCategoryRow,
  DocAccessDocumentRow,
  DocAccessEffect,
  DocAccessMode,
  DocAccessReason,
  DocAccessTree,
} from "@xop/dashboard-shared";

/**
 * A second implementation of the access decision, in the browser.
 *
 * A second implementation is normally the bug this whole feature was written
 * to remove, so it needs a reason. The reason is that staff edit a TREE and
 * save once: toggling a folder to "Block" has to grey out forty rows
 * immediately, and forty round-trips (or one round-trip per keystroke of
 * thought) is not an interface anyone would use. The alternative — showing
 * stale pills until Save — is worse, because the pill is the only thing that
 * tells you what your edit actually did.
 *
 * The safety property is that this is NEVER authoritative. The server
 * recomputes on every read and every write; this only previews. And
 * `verdict.test.ts` runs this function against the SAME fixtures as the
 * Rails `access_resolver_spec`, so a drift between the two fails CI rather
 * than shipping a UI that lies.
 *
 * The layer order is identical to Documents::AccessResolver:
 *   1. library   — published, and in a folder published to this portal —
 *                  EXCEPT release documentation (`shelf: "release"`, a
 *                  document pinned to a release), whose folder is a label:
 *                  it passes on `published` alone (2026-09-13)
 *   2. rules     — nearest wins, walking up the category tree
 *   2b. reseller — customer portal only (2026-09-30): the rule the
 *                  customer's RESELLER set, bounded by what that reseller
 *                  can read itself. The browser cannot recompute it (it
 *                  knows neither the reseller chain nor the reseller's own
 *                  view), so an unruled row keeps the SERVER's reseller
 *                  verdict — and a staff rule, stored or drafted, still
 *                  wins over it because layer 2 runs first.
 *   3. entitlement — or, in strict mode, "no rule" means no
 */

export interface LocalVerdict {
  visible: boolean;
  reason: DocAccessReason;
  detail: string | null;
}

/** Pending edits keyed as `${target_type}:${target_id}`. `null` = cleared
 * back to Auto, which is the ABSENCE of a rule rather than a third state. */
export type RuleDraft = Map<string, DocAccessEffect | null>;

export function ruleKey(
  targetType: "Document" | "DocumentCategory",
  targetId: number,
): string {
  return `${targetType}:${targetId}`;
}

interface ResolveInput {
  tree: DocAccessTree;
  draft: RuleDraft;
  mode: DocAccessMode;
  portal: "customer" | "reseller";
}

/**
 * The effective rule for a target, drafts layered over what the server
 * returned. Returns `undefined` for "no rule", distinct from a draft that
 * explicitly cleared one.
 */
function effectiveEffect(
  draft: RuleDraft,
  stored: { effect: DocAccessEffect } | null,
  targetType: "Document" | "DocumentCategory",
  targetId: number,
): DocAccessEffect | undefined {
  const key = ruleKey(targetType, targetId);
  if (draft.has(key)) {
    const drafted = draft.get(key);
    return drafted ?? undefined;
  }
  return stored?.effect;
}

/** Nearest folder first, then outward. Depth-capped for the same reason the
 * server caps it: a cycle from a bad import must not hang the render. */
function ancestorChain(
  categoryId: number | null,
  parentOf: Map<number, number | null>,
): number[] {
  if (categoryId == null) return [];

  const chain: number[] = [];
  const seen = new Set<number>();
  let current: number | null = categoryId;

  while (current != null && !seen.has(current) && chain.length < 50) {
    seen.add(current);
    chain.push(current);
    current = parentOf.get(current) ?? null;
  }

  return chain;
}

export function resolveTree(input: ResolveInput): Map<number, LocalVerdict> {
  const { tree, draft, mode, portal } = input;

  const parentOf = new Map<number, number | null>(
    tree.categories.map((c) => [c.id, c.parent_id]),
  );
  const categoryById = new Map<number, DocAccessCategoryRow>(
    tree.categories.map((c) => [c.id, c]),
  );

  const out = new Map<number, LocalVerdict>();

  for (const doc of tree.documents) {
    out.set(doc.id, resolveDocument({ doc, draft, mode, portal, parentOf, categoryById }));
  }

  return out;
}

function resolveDocument(args: {
  doc: DocAccessDocumentRow;
  draft: RuleDraft;
  mode: DocAccessMode;
  portal: "customer" | "reseller";
  parentOf: Map<number, number | null>;
  categoryById: Map<number, DocAccessCategoryRow>;
}): LocalVerdict {
  const { doc, draft, mode, portal, parentOf, categoryById } = args;

  // ── 1. library ──────────────────────────────────────────────────────
  if (doc.status !== "published") {
    return { visible: false, reason: "unpublished", detail: null };
  }

  // A private document belongs to ONE customer organization, and a reseller
  // is never that organization. That makes it a LIBRARY fact rather than an
  // entitlement one, so it sits here — above rules — and no allow toggle can
  // hand it over. Mirrors Documents::AccessResolver#library_scope.
  if (portal === "reseller" && doc.access_scope === "private") {
    return { visible: false, reason: "not_entitled", detail: "private to one customer" };
  }

  // A training is never a customer's, and no rule reopens it. For a reseller
  // its folder is a label, like release documentation's. Mirrors
  // Documents::AccessResolver#library_verdict (2026-09-17).
  const training = doc.purpose === "training";
  if (training && portal === "customer") {
    return { visible: false, reason: "training", detail: "trainings are for resellers" };
  }

  // Release documentation: attaching a guide to a release IS the access
  // decision, so the folder is not consulted. Mirrors
  // Documents::AccessResolver#library_verdict.
  const releaseShelf = doc.shelf === "release" || doc.default_release_id != null;

  if (!releaseShelf && !training) {
    if (doc.category_id == null) {
      // The customer portal shows unfiled documents (they are in no hidden
      // bucket); the reseller portal hides them, because "nobody filed it"
      // reads as "not curated for a partner". Asymmetric on purpose.
      if (portal === "reseller") {
        return { visible: false, reason: "uncategorized", detail: null };
      }
    } else {
      const category = categoryById.get(doc.category_id);
      if (category && !category.published_to_portal) {
        return { visible: false, reason: "folder_unpublished", detail: null };
      }
    }
  }

  // ── 2. rules ────────────────────────────────────────────────────────
  const own = effectiveEffect(draft, doc.rule, "Document", doc.id);
  if (own) {
    return {
      visible: own === "allow",
      reason: own === "allow" ? "rule_allow" : "rule_deny",
      detail: "document",
    };
  }

  for (const categoryId of ancestorChain(doc.category_id, parentOf)) {
    const category = categoryById.get(categoryId);
    const effect = effectiveEffect(draft, category?.rule ?? null, "DocumentCategory", categoryId);
    if (effect) {
      return {
        visible: effect === "allow",
        reason: effect === "allow" ? "rule_allow" : "rule_deny",
        detail: "folder",
      };
    }
  }

  // ── 2b. the reseller layer ──────────────────────────────────────────
  // No staff rule decides this row, and the server says the customer's
  // reseller did. Keep that answer: it sits ABOVE strict mode and
  // entitlement, so neither a mode switch nor the product list changes it.
  // Mirrors Documents::AccessResolver's reseller layer.
  if (doc.reason === "reseller_allow" || doc.reason === "reseller_deny") {
    return { visible: doc.visible, reason: doc.reason, detail: doc.reason_detail };
  }

  // Below rules, above strict mode and entitlement: every reseller reads a
  // training unless a rule above blocked it.
  if (training) {
    return { visible: true, reason: "training", detail: null };
  }

  // ── 3. entitlement ──────────────────────────────────────────────────
  if (mode === "strict") {
    return { visible: false, reason: "not_whitelisted", detail: null };
  }

  // With no rule and no mode change, the server's own answer still stands —
  // entitlement depends on the account's products, which the browser has no
  // business recomputing. Reusing the server verdict here is what keeps this
  // a PREVIEW of an edit rather than a parallel gate.
  return { visible: doc.visible, reason: doc.reason, detail: doc.reason_detail };
}

/** Human sentence for a pill. Kept beside the resolver so a new reason
 * cannot be added without someone deciding what it says. */
export function reasonLabel(verdict: LocalVerdict): string {
  switch (verdict.reason) {
    case "unpublished":
      return "Draft — not published to anyone";
    case "folder_unpublished":
      return "Folder not published to this portal";
    case "uncategorized":
      return "Not filed in a folder";
    case "rule_allow":
      return verdict.detail === "folder" ? "Allowed — folder" : "Allowed";
    case "rule_deny":
      return verdict.detail === "folder" ? "Blocked — folder" : "Blocked";
    case "not_whitelisted":
      return "Not on the whitelist";
    case "general":
      return "Open to everyone";
    case "product":
      return verdict.detail ? `Product ${verdict.detail}` : "Product";
    case "feature":
      return verdict.detail ? `Feature ${verdict.detail}` : "Feature";
    case "release":
      return verdict.detail === "every reseller" ? "Release documentation — every reseller" : "Release documentation — on this release";
    case "private":
      return "Private to this account";
    case "not_entitled":
      return "Product not purchased";
    case "training":
      return verdict.visible ? "Training — every reseller" : "Training — resellers only";
    case "reseller_allow":
      return "Shared by its reseller";
    case "reseller_deny":
      return "Hidden by its reseller";
    default:
      return "Hidden";
  }
}

/** Turn the draft into the payload the API expects. Only genuinely changed
 * targets are sent — an unchanged tree saves nothing, which matters because
 * "Save" on this page rewrites an access control. */
export function draftToPayload(
  tree: DocAccessTree,
  draft: RuleDraft,
): { target_type: "Document" | "DocumentCategory"; target_id: number; effect: DocAccessEffect | null }[] {
  const storedByKey = new Map<string, DocAccessEffect>();
  for (const c of tree.categories) {
    if (c.rule) storedByKey.set(ruleKey("DocumentCategory", c.id), c.rule.effect);
  }
  for (const d of tree.documents) {
    if (d.rule) storedByKey.set(ruleKey("Document", d.id), d.rule.effect);
  }

  const out: { target_type: "Document" | "DocumentCategory"; target_id: number; effect: DocAccessEffect | null }[] = [];

  for (const [key, effect] of draft) {
    const [rawType, rawId] = key.split(":");
    if (!rawType || !rawId) continue;

    const targetType = rawType as "Document" | "DocumentCategory";
    const targetId = Number(rawId);
    const stored = storedByKey.get(key) ?? null;

    if ((effect ?? null) === stored) continue;

    out.push({ target_type: targetType, target_id: targetId, effect: effect ?? null });
  }

  return out;
}
